import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import {
  ClientFullModel,
  DateRange,
  HxCurrencyPipe,
  HxFixedPipe,
  HxMoneyFormatPipe,
  HxReportService,
  HxUiLabelPipe,
  isoDate,
  SourceSystem,
  StatisticsCategoryPopularProductsModel,
  StatisticsCategoryProductsTimeModel,
  StatisticsPaymentTypesModel,
  StatisticsProductSalesModel,
  StatisticsProductSourceTypesModel,
  toFixed,
  toRouterQueryParams
} from 'hx-services';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, Subscription } from 'rxjs';
import { XYChart } from '@amcharts/amcharts5/xy';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { subDays, subMonths } from 'date-fns';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { HxDateRangeSelectComponent } from 'hx-component';
import { FormsModule } from '@angular/forms';

type ViewType = 'revenue' | 'profit' | 'receipts' | 'visitors' | 'avgReceipt';
type RangeType = 'day' | 'week' | 'month';
type PageType = 'product-categories' | 'products' | 'abc-analysis' | null;

interface TempArrayModel {
  date: string,
  value: number
}

interface OrdersDayOfWeekModel extends StatisticsCategoryProductsTimeModel {
  dayOfWeekStr?: string;
}

@Component({
    selector: 'app-product-sales',
    templateUrl: './product-sales.component.html',
    styleUrls: ['./product-sales.component.css'],
    imports: [NgIf, RouterLink, HxDateRangeSelectComponent, FormsModule, NgClass, NgFor, DatePipe, TranslocoPipe, HxCurrencyPipe, HxUiLabelPipe, HxFixedPipe, HxMoneyFormatPipe]
})
export class ProductSalesComponent implements OnInit, OnDestroy {
  params: {
    date?: DateRange,
    categoryId?: number,
    productInfoId?: number,
  }= {};
  statView: ViewType = 'revenue';
  rangeType: RangeType = 'day';
  sales: StatisticsProductSalesModel[] = [];
  paymentTypes: StatisticsPaymentTypesModel;
  sourceTypes: StatisticsProductSourceTypesModel[] = [];
  leftPopularProducts: StatisticsCategoryPopularProductsModel[] = [];
  rightPopularProducts: StatisticsCategoryPopularProductsModel[] = [];
  ordersTime: StatisticsCategoryProductsTimeModel[] = [];
  ordersDayOfWeek: OrdersDayOfWeekModel[] = [];
  salesCurrentDay: StatisticsProductSalesModel;
  isloading = {
    sales: false,
    paymentTypes: false,
    sourceTypes: false,
    popularProducts: false,
    ordersTime: false,
    ordersDayOfWeek: false,
  };
  data: { date: string, value: number }[] = [];
  now = isoDate(new Date());
  dataSourceTypes: {sourceType: string, total: number, type?: SourceSystem}[] = [];
  sourceTypeClicked = false;
  revenueTotal: number = 0;
  profitTotal: number = 0;
  receiptsTotal: number = 0;
  visitorsTotal: number = 0;
  avgReceiptTotal: number = 0;
  dayOfWeekMap: Map<number, string> = new Map<number, string>();
  pageType: PageType;
  client?: ClientFullModel;
  categoryTitle?: string;
  productTitle?: string;
  private sub!: Subscription;
  private chartSourceType: XYChart;
  private chartSale: XYChart;
  private chartOrdersTime: XYChart;
  private chartOrdersDayOfWeek: XYChart;

  constructor(
    private reportService: HxReportService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private zone: NgZone,
    private tr:TranslocoService,
  ) {
  }

  browserOnly(fn: () => void) {
    this.zone.runOutsideAngular(() => fn());
  }

  async ngOnInit() {
    this.pageType = this.aRoute.snapshot.paramMap.get('type') as PageType;
    this.sub = this.aRoute.queryParamMap.subscribe(async paramMap => {
      if (this.pageType) {
        this.params.date = { from: paramMap.get('dateFrom'), to: paramMap.get('dateTo') };
        this.params.categoryId = paramMap.get('categoryId') ? Number(paramMap.get('categoryId')) : undefined;
        this.params.productInfoId = paramMap.get('productInfoId') ? Number(paramMap.get('productInfoId')) : undefined;
      } else {
        this.params.date = { from: isoDate(subDays(subMonths(new Date(), 1), 1)), to: isoDate(subDays(new Date(), 1)) };
      }
      if (this.pageType === 'product-categories') {
        this.categoryTitle = paramMap.get('categoryTitle');
      } else if (this.pageType === 'products' || this.pageType === 'abc-analysis') {
        this.productTitle = paramMap.get('productTitle');
      }
      await firstValueFrom(this.tr.selectTranslateObject('demo'));
      this.initDayOfWeekMap();
      await Promise.all([
        this.loadProductSales(),
        this.loadCurrentDaySales(),
        this.loadSourceTypes(),
        this.loadPopularProducts(),
        this.loadOrdersTime(),
        this.loadOrdersDayOfWeek(),
      ]);

      if (!this.chartSale) {
        this.createChart();
      }
      this.loadChart();
      this.fillSourceType();
      this.fillOrdersTime();
      this.fillOrdersDayOfWeek();
    });
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    this.disposeCharts();
  }

  applyFilters() {
    this.navigate();
  }

  print() {
    const element = document.getElementById('contentToPrint') as HTMLElement;

    html2canvas(element).then(canvas => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl);
    });
  }

  changeGraphInfo(viewType: ViewType) {
    this.statView = viewType;
    this.rangeType = 'day';
    this.loadChart();
  }

  async changeRangeView(rangeType: RangeType) {
    this.rangeType = rangeType;
    this.loadChart();
  }

  receipts() {
    this.sourceTypeClicked = !this.sourceTypeClicked;
    this.receiptSourceType();
    this.fillSourceType();
  }

  revenue(){
    this.sourceTypeClicked = !this.sourceTypeClicked;
    this.revenueSourceType();
    this.fillSourceType();
  }

  private navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  private async createChart() {
    Promise.all([
      import('@amcharts/amcharts5/index'),
      import('@amcharts/amcharts5/xy'),
      import('@amcharts/amcharts5/themes/Animated').then(res => res.default),
      import('@amcharts/amcharts5/locales/ru_RU').then(res => res.default),
    ]).then(([am5, am5xy, am5themes_Animated, am5ru]) => {
      //Source Type chart
      {
        const root = am5.Root.new('sourceTypeChart');
        root.setThemes([
          am5themes_Animated.new(root)
        ]);

        const chart = root.container.children.push(am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: 'panX',
          wheelY: 'zoomX',
          paddingLeft: 0,
          layout: root.verticalLayout
        }));

        const yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(root, {
          categoryField: 'sourceType',
          renderer: am5xy.AxisRendererY.new(root, {
            inversed: true,
          })
        }));
        yAxis.data.clear();
        const data = this.dataSourceTypes;
        yAxis.data.setAll(data);

        const xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererX.new(root, {}),
        }));

        const yRendered = yAxis.get('renderer');
        yRendered.grid.template.set('visible', false);

        const xRendered = xAxis.get('renderer');
        xRendered.labels.template.set('visible', false);
        xRendered.grid.template.set('visible', false);

        const series = chart.series.push(am5xy.ColumnSeries.new(root, {
          xAxis: xAxis,
          yAxis: yAxis,
          valueXField: 'total',
          categoryYField: 'sourceType',
          sequencedInterpolation: true,
          tooltip: am5.Tooltip.new(root, {
            pointerOrientation: 'horizontal',
            labelText: '[bold]{categoryY}: {valueX}'
          })
        }));

        series.columns.template.setAll({
          height: am5.percent(30),
          dy: -5,
          width: am5.percent(70),
        });

        series.bullets.push(function () {
          return am5.Bullet.new(root, {
            locationX: 1,
            sprite: am5.Label.new(root, {
              centerY: am5.p50,
              text: '{valueX}',
              populateText: true,
            })
          });
        });
        series.data.clear();
        series.data.setAll(data);
        series.appear();

        const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {
          behavior: 'zoomY'
        }));
        cursor.lineY.set('forceHidden', true);
        cursor.lineX.set('forceHidden', true);
        chart.appear(1000, 100);
        this.chartSourceType = chart;
      }
      //Chart by Week
      {
        const root = am5.Root.new('chartByWeek');

        root.setThemes([
          am5themes_Animated.new(root)
        ]);

        const chart = root.container.children.push(
          am5xy.XYChart.new(root, {
            panX: true,
            panY: true,
            wheelX: 'panX',
            wheelY: 'zoomX',
            pinchZoomX: true
          })
        );

        const xAxis = chart.xAxes.push(
          am5xy.CategoryAxis.new(root, {
            maxDeviation: 0.3,
            categoryField: 'dayOfWeekStr',
            renderer: am5xy.AxisRendererX.new(root, {
              minGridDistance: 30
            }),
            tooltip: am5.Tooltip.new(root, {})
          })
        );

        const yAxis = chart.yAxes.push(
          am5xy.ValueAxis.new(root, {
            renderer: am5xy.AxisRendererY.new(root, {})
          })
        );

        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'valueTotal',
            sequencedInterpolation: true,
            categoryXField: 'dayOfWeekStr',
            tooltip: am5.Tooltip.new(root, {
              labelText: '{valueY}'
            })
          })
        );

        this.ordersDayOfWeek.sort((a, b) => a.value - b.value);
        const data = this.ordersDayOfWeek;

        xAxis.data.setAll(data);
        series.data.setAll(data);

        chart.set('cursor', am5xy.XYCursor.new(root, {}));
        this.chartOrdersDayOfWeek = chart;
      }
      //Chart by time
      {
        const root = am5.Root.new('chartByTyme');

        root.setThemes([
          am5themes_Animated.new(root)
        ]);

        const chart = root.container.children.push(
          am5xy.XYChart.new(root, {
            panX: true,
            panY: true,
            wheelX: 'panX',
            wheelY: 'zoomX',
            pinchZoomX: true
          })
        );

        const xAxis = chart.xAxes.push(
          am5xy.CategoryAxis.new(root, {
            maxDeviation: 0.3,
            categoryField: 'value',
            renderer: am5xy.AxisRendererX.new(root, {
              minGridDistance: 30
            }),
            tooltip: am5.Tooltip.new(root, {})
          })
        );

        const yAxis = chart.yAxes.push(
          am5xy.ValueAxis.new(root, {
            renderer: am5xy.AxisRendererY.new(root, {})
          })
        );

        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'valueTotal',
            sequencedInterpolation: true,
            categoryXField: 'value',
            tooltip: am5.Tooltip.new(root, {
              labelText: '{valueY}'
            })
          })
        );
        const data = this.ordersTime;

        xAxis.data.setAll(data);
        series.data.setAll(data);

        chart.set('cursor', am5xy.XYCursor.new(root, {}));
        this.chartOrdersTime = chart;
      }
      //Chart Sales
      {
        const root = am5.Root.new('chartdiv');
        root.locale = am5ru;
        root.setThemes([
          am5themes_Animated.new(root)
        ]);
        root.dateFormatter.setAll({
          dateFormat: 'yyyy',
          dateFields: ['valueX'],
        });

        const chart = root.container.children.push(am5xy.XYChart.new(root, {
          focusable: true,
          panX: true,
          panY: true,
          wheelX: 'panX',
          wheelY: 'zoomX',
          pinchZoomX: true,
          paddingLeft: 0,
        }));
        const easing = am5.ease.linear;

        const xAxis = chart.xAxes.push(am5xy.DateAxis.new(root, {
          maxDeviation: 0.1,
          groupData: false,
          baseInterval: {
            timeUnit: 'day',
            count: 1
          },
          renderer: am5xy.AxisRendererX.new(root, {
            minorGridEnabled: true,
            minGridDistance: 70
          }),
          tooltip: am5.Tooltip.new(root, {})
        }));

        xAxis.get('renderer').labels.template.setAll({
          rotation: -90,
          centerY: am5.p50,
          centerX: am5.p50,
          paddingTop: 10,
          fontSize: 10,
        });

        xAxis.set('dateFormatter', am5.DateFormatter.new(root, {
          dateFormat: 'yyyy-MM-dd' // Customize the format here
        }));

        const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
          maxDeviation: 0.2,
          renderer: am5xy.AxisRendererY.new(root, {})
        }));

        const series = chart.series.push(am5xy.LineSeries.new(root, {
          minBulletDistance: 7,
          connect: true,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'value',
          valueXField: 'date',
          tooltip: am5.Tooltip.new(root, {
            pointerOrientation: 'horizontal',
            labelText: '{valueY}'
          })
        }));

        series.fills.template.setAll({
          fillOpacity: 0.2,
          visible: true
        });

        series.strokes.template.setAll({
          strokeWidth: 2
        });

        series.data.processor = am5.DataProcessor.new(root, {
          dateFormat: 'yyyy-MM-dd',
          dateFields: ['date']
        });

        series.data.setAll(this.data);

        series.bullets.push(function() {
          const circle = am5.Circle.new(root, {
            radius: 4,
            fill: root.interfaceColors.get('background'),
            stroke: series.get('fill'),
            strokeWidth: 2
          });

          return am5.Bullet.new(root, {
            sprite: circle
          });
        });

        const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {
          xAxis: xAxis,
          behavior: 'none'
        }));
        cursor.lineY.set('visible', false);
        chart.appear(1000, 100);
        this.chartSale = chart;

        this.changeRangeView('day');
      }
    }).catch((e) => {
      console.error('Error when creating chart', e);
    });
  }

  private async loadProductSales() {
    this.sales = [];
    this.isloading.sales = true;
    try {
      this.sales = await this.reportService.getStatisticsProductSales({fromDate: this.params.date.from, toDate: this.params.date.to, categoryId: this.params.categoryId, productInfoId: this.params.productInfoId });
      this.revenueTotal = this.sales.reduce((a, b) => a + b.valueTotal, 0);
      this.profitTotal = this.sales.reduce((a, b) => a + b.costPriceTotal, 0);
      this.receiptsTotal = this.sales.reduce((a, b) => a + b.orderCount, 0);
      this.visitorsTotal = this.sales.reduce((a, b) => a + (b.orderCount), 0);
      this.avgReceiptTotal = toFixed(this.revenueTotal / this.receiptsTotal);
    } finally {
      this.isloading.sales = false;
    }
  }

  private async loadCurrentDaySales() {
    this.isloading.sales = true;
    try {
      const sales = await this.reportService.getStatisticsProductSales({fromDate: isoDate(new Date()), toDate: isoDate(new Date()), categoryId: this.params.categoryId, productInfoId: this.params.productInfoId });
      if (sales.length > 0) {
        this.salesCurrentDay = sales[0];
      } else {
        this.salesCurrentDay = undefined;
      }
    } finally {
      this.isloading.sales = false;
    }
  }

  private async loadSourceTypes() {
    this.isloading.sourceTypes = true;
    try {
      const st = await this.reportService.getStatisticsProductSourceTypes({fromDate: this.params.date.from, toDate: this.params.date.to, categoryId: this.params.categoryId, productInfoId: this.params.productInfoId });
      if (st.length > 0) {
        this.sourceTypes = st;
        this.revenueSourceType();
      } else {
        this.sourceTypes = undefined;
      }
    } finally {
      this.isloading.sourceTypes = false;
    }
  }

  private async loadPopularProducts() {
    this.leftPopularProducts = [];
    this.rightPopularProducts = [];
    this.isloading.popularProducts = true;
    try {
      const popularProducts = await this.reportService.getStatisticsCategoryPopularProducts({fromDate: this.params.date.from, toDate: this.params.date.to, categoryId: this.params.categoryId, productInfoId: this.params.productInfoId });
      if (popularProducts.length > 0) {
        popularProducts.sort((a, b) => b.total - a.total);
        this.leftPopularProducts = popularProducts.slice(0, 4);
        this.rightPopularProducts = popularProducts.slice(4, 8);
      }
    } finally {
      this.isloading.popularProducts = false;
    }
  }

  private async loadOrdersTime() {
    this.ordersTime = [];
    this.isloading.ordersTime = true;
    try {
      this.ordersTime = await this.reportService.getStatisticsCategoryProductsTime({fromDate: this.params.date.from, toDate: this.params.date.to, categoryId: this.params.categoryId, productInfoId: this.params.productInfoId });
      for (let i in Array.from(Array(23))) {
        if (this.ordersTime.map(r => r.value).includes(Number(i))) {
           this.ordersTime.push({ value: Number(i), valueTotal: 0 });
        }
      }
      this.ordersTime.sort((a, b) => a.value - b.value);
    } finally {
      this.isloading.ordersTime = false;
    }
  }

  private async loadOrdersDayOfWeek() {
    this.ordersDayOfWeek = [];
    this.isloading.ordersDayOfWeek = true;
    try {
      this.ordersDayOfWeek = await this.reportService.getStatisticsCategoryProductsDayOfWeek({fromDate: this.params.date.from, toDate: this.params.date.to, categoryId: this.params.categoryId, productInfoId: this.params.productInfoId });
      for (let i: number = 1; i < 8; i++) {
        if (!this.ordersDayOfWeek.map(r => r.value).includes(Number(i))) {
          this.ordersDayOfWeek.push({ value: Number(i), valueTotal: 0 });
        }
      }
      this.ordersDayOfWeek.sort((a, b) => a.value - b.value);
      this.ordersDayOfWeek.forEach(r => r.dayOfWeekStr = this.dayOfWeekMap.get(r.value));
    } finally {
      this.isloading.ordersDayOfWeek = false;
    }
  }

  private loadChart() {
    this.data = [];
    if (this.rangeType === 'day') {
      this.fillData();
      this.chartSale?.series.getIndex(0).data.clear();
      this.chartSale?.series.getIndex(0).data.setAll(this.data);
    } else if (this.rangeType === 'week') {
      this.fillData();
      const weeklySummaries = this.summarizeTotalsByWeek(this.data);
      this.chartSale?.series.getIndex(0).data.clear();
      this.chartSale?.series.getIndex(0).data.setAll(weeklySummaries);
    } else if (this.rangeType === 'month') {
      this.fillData();
      const monthlySummaries = this.summarizeByFirstDateOfMonth(this.data);
      this.chartSale?.series.getIndex(0).data.clear();
      this.chartSale?.series.getIndex(0).data.setAll(monthlySummaries);
    }
  }

  private fillData() {
    if (this.statView === 'revenue') {
      this.sales.forEach(r => {
        this.data.push({ date: r.paymentDate, value: r.valueTotal });
      });
    } else if (this.statView === 'profit') {
      this.sales.forEach(r => {
        this.data.push({ date: r.paymentDate, value: r.costPriceTotal });
      });
    } else if (this.statView === 'receipts') {
      this.sales.forEach(r => {
        this.data.push({ date: r.paymentDate, value: r.orderCount });
      });
    } else if (this.statView === 'visitors') {
      this.sales.forEach(r => {
        this.data.push({ date: r.paymentDate, value: r.orderCount });
      });
    } else if (this.statView === 'avgReceipt') {
      this.sales.forEach(r => {
        this.data.push({ date: r.paymentDate, value: toFixed(r.valueTotal/r.costPriceTotal, 2) });
      });
    }
  }

  private receiptSourceType() {
    this.dataSourceTypes = [];
    this.sourceTypes.forEach(r => {
      if ((r.orderCount ?? 0) > 0) {
        this.dataSourceTypes.push({ sourceType: this.tr.translate('sourceSystem.' + r.from), total: r.orderCount });
      }
    });
    this.dataSourceTypes.sort((a, b) => b.total - a.total);
  }

  private revenueSourceType() {
    this.dataSourceTypes = [];

    this.sourceTypes.forEach(r => {
      this.dataSourceTypes.push({ sourceType: this.tr.translate('sourceSystem.' + r.from), total: r.total, type: r.from });
    });
    Object.values([SourceSystem.CASHBOX, SourceSystem.CALLCENTER, SourceSystem.MANAGER, SourceSystem.ONLINE, SourceSystem.ONLINE_APP, SourceSystem.TELEGRAM]).forEach(r => {
      if (!this.dataSourceTypes.map(t => t.type).includes(r)) {
        this.dataSourceTypes.push({ sourceType: this.tr.translate('sourceSystem.' + r), total: 0 });
      }
    });
    this.dataSourceTypes.sort((a, b) => b.total - a.total);
  }

  private fillSourceType() {
    this.chartSourceType?.series.getIndex(0).data.clear();
    this.chartSourceType?.series.getIndex(0).data.setAll(this.dataSourceTypes);
  }

  private groupDaysByWeeks(days: TempArrayModel[]): TempArrayModel[][] {
    const weeks: TempArrayModel[][] = [];
    let currentWeek: TempArrayModel[] = [];

    days.forEach(day => {
      const date = day.date;
      if (new Date(date).getDay() === 0 && currentWeek.length > 0) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
      currentWeek.push(day);
    });
    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }
    return weeks;
  }

  private summarizeTotalsByWeek(days: TempArrayModel[]): TempArrayModel[] {
    const weeks = this.groupDaysByWeeks(days);
    return weeks.map(week => {
      const date = week[0].date;
      const value = week.reduce((sum, day) => sum + day.value, 0);
      return { date, value };
    });
  }

  private summarizeByFirstDateOfMonth(data: TempArrayModel[]): TempArrayModel[] {
    const monthlyTotals: { [key: string]: number } = {};
    data.forEach(entry => {
      const firstDateOfMonth = new Date(new Date(entry.date).getFullYear(), new Date(entry.date).getMonth() + 1, 1);
      const key = firstDateOfMonth.toISOString().slice(0, 7); // Format: 'YYYY-MM'

      if (!monthlyTotals[key]) {
        monthlyTotals[key] = 0;
      }
      monthlyTotals[key] += entry.value;
    });
    return Object.keys(monthlyTotals).map(key => ({
      date: isoDate(new Date(key + '-01')),
      value: monthlyTotals[key],
    }));
  }

  private fillOrdersTime() {
    this.chartOrdersTime?.series.getIndex(0).data.clear();
    this.chartOrdersTime?.series.getIndex(0).data.setAll(this.ordersTime);
  }

  private fillOrdersDayOfWeek() {
    this.chartOrdersDayOfWeek?.series.getIndex(0).data.clear();
    this.chartOrdersDayOfWeek?.series.getIndex(0).data.setAll(this.ordersDayOfWeek);
  }

  private initDayOfWeekMap() {
    this.dayOfWeekMap.set(1, this.tr.translate('day-of-week.short.Monday'));
    this.dayOfWeekMap.set(2, this.tr.translate('day-of-week.short.Tuesday'));
    this.dayOfWeekMap.set(3, this.tr.translate('day-of-week.short.Wednesday'));
    this.dayOfWeekMap.set(4, this.tr.translate('day-of-week.short.Thursday'));
    this.dayOfWeekMap.set(5, this.tr.translate('day-of-week.short.Friday'));
    this.dayOfWeekMap.set(6, this.tr.translate('day-of-week.short.Saturday'));
    this.dayOfWeekMap.set(7, this.tr.translate('day-of-week.short.Sunday'));
  }


  private disposeCharts() {
    this.browserOnly(() => {
      this.chartSale?.dispose();
      this.chartOrdersTime?.dispose();
      this.chartOrdersDayOfWeek?.dispose();
      this.chartSourceType?.dispose();
    });
  }
}
